import { Form, Select } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React from "react"
import { useTranslation } from "react-i18next"

import { CustomDatePicker } from "../../../../components/Date&TimePickers/CustomDatePicker.tsx"
import { CustomTimePicker } from "../../../../components/Date&TimePickers/CustomTimePicker.tsx"
import { FollowUpTimePicker } from "../../../../components/Date&TimePickers/FollowUpTimePicker.tsx"
import { ResolvedSelect } from "../../../../components/Select/ResolvedSelect.tsx"
import { UNRESOLVED_USER_LABEL } from "../../../../utils/select/entity-labels.ts"
import { durationOptions } from "./schedule-durations.ts"

type LeadScheduleKind = "followUp" | "appointment"

/**
 * The one set of fields behind every lead scheduling form. The operational
 * states modal carried its own date and time pickers until issue 27, which is
 * how a follow-up booked there ended up unable to set a length.
 *
 * Field names are the generic `date`/`time`/`duration`; each form maps them
 * onto the lead's own columns when it submits.
 */
interface LeadScheduleFieldsProps {
  kind: LeadScheduleKind
  owners?: BaseOptionType[]
  showOwner?: boolean
  showDate?: boolean
  showTime?: boolean
  required?: boolean
  /**
   * Whether the owner can be unset. Defaults to "only while it is optional",
   * which is right for a form that exists to schedule. A form where the slot
   * becomes required only because the user started filling it must keep the
   * clear, or there is no way back out of scheduling.
   */
  ownerClearable?: boolean
}

export function LeadScheduleFields({
  kind,
  owners = [],
  showOwner = kind === "followUp",
  showDate = true,
  showTime = true,
  required = false,
  ownerClearable = !required
}: LeadScheduleFieldsProps) {
  const { t } = useTranslation()
  const isFollowUp = kind === "followUp"

  const dateLabel = isFollowUp
    ? t("leadWorkspace.header.fields.followUpDate")
    : t("leadWorkspace.header.fields.appointmentDate")
  const timeLabel = isFollowUp
    ? t("leadWorkspace.header.fields.followUpTime")
    : t("leadWorkspace.header.fields.appointmentTime")

  return (
    <>
      {showOwner && (
        <Form.Item
          preserve={false}
          name={"follow_up_owner"}
          label={t("leadWorkspace.header.fields.followUpOwner")}
          rules={
            required
              ? [
                  {
                    required: true,
                    message: t("leadWorkspace.header.validation.followUpOwner")
                  }
                ]
              : undefined
          }
        >
          <ResolvedSelect
            allowClear={ownerClearable}
            showSearch
            optionFilterProp={"label"}
            options={owners}
            fallbackLabel={UNRESOLVED_USER_LABEL}
            placeholder={t("leadWorkspace.header.owner.placeholder")}
          />
        </Form.Item>
      )}

      {showDate && (
        <Form.Item
          preserve={false}
          name={"date"}
          label={dateLabel}
          rules={
            required
              ? [
                  {
                    required: true,
                    message: t("leadWorkspace.header.validation.followUpDate")
                  }
                ]
              : undefined
          }
        >
          <CustomDatePicker className={"w-full"} />
        </Form.Item>
      )}

      {showTime && (
        <>
          <Form.Item
            preserve={false}
            name={"time"}
            label={timeLabel}
            rules={
              required
                ? [
                    {
                      required: true,
                      message: t("leadWorkspace.header.validation.followUpTime")
                    }
                  ]
                : undefined
            }
          >
            {isFollowUp ? (
              <FollowUpTimePicker className={"w-full"} />
            ) : (
              <CustomTimePicker className={"w-full"} />
            )}
          </Form.Item>

          {/* A length with no start time would mean nothing, so the two travel
              together — Rehash / Nurture dates a return without booking one. */}
          <Form.Item
            preserve={false}
            name={"duration"}
            label={t("leadWorkspace.header.fields.duration")}
          >
            <Select className={"w-full"} options={durationOptions(t)} />
          </Form.Item>
        </>
      )}
    </>
  )
}

export { type LeadScheduleKind }
